/**
 * Canonical charge computation — lib (frontend) side.
 *
 * ESPEJO EXACTO de functions/src/utils/charge.ts — editar AMBOS + shared-fixtures/charge-cases.json.
 *
 * Design rules (ADR — feat/canonical-charge Task 1):
 *   - amountCLP is the canonical INTEGER: lodgeCLP + round(activitiesUSD × rate).
 *   - amountUSD is DERIVED: round((amountCLP / rate) × 100) / 100.
 *   - ONE conversion point per direction — never chain rounds.
 *   - activitiesUSD is clamped to ≥ 0; a discount larger than the activities
 *     bucket spills onto the lodge CLP so it never silently evaporates.
 *   - discountCLP (a coupon authored in pesos) is subtracted from lodgeCLP as an
 *     INTEGER, before any conversion — it must land to the exact peso.
 *
 * This file intentionally has NO Firebase / Next.js imports.
 * It is a pure computation module usable in both server and client components.
 */

import { BookingCharge, ChargeLine } from '../../types/booking';

// ─── Input contract ───────────────────────────────────────────────────────────

export interface ChargeInput {
  /** Lodge nights or unit stays, each with their Guesty CLP price (integer) */
  lodges: Array<{ ref: string; clp: number }>;
  /** Activities priced in USD (their native currency) */
  activities: Array<{ ref: string; usd: number }>;
  /** Services (transport, extras) priced in USD */
  services: Array<{ ref: string; usd: number }>;
  /** Platform service fee in USD (added before discount) */
  serviceFeeUSD: number;
  /** Discount in USD. Absorbed by activities+services+fee first; any excess
   *  spills onto the lodge CLP so a lodge-only discount is never lost. */
  discountUSD: number;
  /**
   * Discount AUTHORED in CLP (integer pesos) — a fixed-amount coupon created in
   * CLP by the admin. Subtracted from `lodgeCLP` BEFORE any currency
   * conversion, so the guest gets the exact pesos the coupon promises
   * (706.703 CLP off 916.400 CLP is 209.697 CLP at ANY rate — never
   * 209.695 because of a CLP→USD→CLP round trip). Only the part that exceeds
   * the lodge bucket crosses to USD (once) and joins `discountUSD`.
   *
   * Callers set EITHER `discountCLP` (fixed-CLP coupon) OR `discountUSD`
   * (percentage / fixed-USD coupon) — both are honoured if both are present.
   */
  discountCLP?: number;
  /** usdToClp full-precision rate (must be > 0) */
  rate: number;
  /** Human-readable source tag for audit (e.g. "fawazahmed0", "fallback") */
  rateSource: string;
  /** Which currency the payment provider will settle in */
  settlementCurrency: 'CLP' | 'USD';
}

// ─── Core function ────────────────────────────────────────────────────────────

/**
 * Compute a canonical, deterministic charge from raw inputs.
 *
 * Algorithm (one rounding boundary per currency direction):
 *   1. Validate rate > 0.
 *   2. lodgeCLP  = round(Σ lodges.clp)     (integer invariant — Guesty may send decimals)
 *   3. discountCLP hits the lodge FIRST, in pesos, with no conversion:
 *        lodgeDiscountCLP = min(round(discountCLP), lodgeCLP)
 *        spilloverUSD     = (round(discountCLP) − lodgeDiscountCLP) / rate
 *   4. activitiesUSD = max(0, Σ activities.usd + Σ services.usd + serviceFeeUSD
 *                             − discountUSD − spilloverUSD)
 *   5. activitiesCLP = Math.round(activitiesUSD × rate)   ← ONLY USD→CLP conversion
 *   6. excessDiscountCLP = Math.round(max(0, discountUSD + spilloverUSD − grossActivitiesUSD) × rate)
 *      amountCLP = max(0, lodgeCLP − lodgeDiscountCLP + activitiesCLP − excessDiscountCLP)
 *      (canonical integer)
 *   7. amountUSD = Math.round((amountCLP / rate) × 100) / 100  ← ONLY CLP→USD conversion
 */
export function computeCanonicalCharge(
  input: ChargeInput
): Omit<BookingCharge, 'version'> & { version: 1 } {
  const { lodges, activities, services, serviceFeeUSD, discountUSD, discountCLP, rate, rateSource, settlementCurrency } = input;

  // Step 1 — validate
  if (rate <= 0) {
    throw new Error('Invalid exchange rate');
  }

  // Step 2 — lodge sum. Rounded to keep the "amountCLP is an integer" invariant
  // even when Guesty returns a fractional subTotalPrice (152.733,33 × 3).
  const lodgeCLP = Math.round(lodges.reduce((acc, l) => acc + l.clp, 0));

  // Step 3 — CLP-authored discount, applied to the lodge in pesos (no conversion).
  // Whatever exceeds the lodge bucket crosses to USD exactly once and is treated
  // like a USD discount from here on.
  const requestedDiscountCLP = Math.max(0, Math.round(discountCLP ?? 0));
  const lodgeDiscountCLP = Math.min(requestedDiscountCLP, lodgeCLP);
  const spilloverDiscountUSD = (requestedDiscountCLP - lodgeDiscountCLP) / rate;

  // Step 4 — activities USD net of discount (clamped ≥ 0).
  // The discount is absorbed by the activities/services/fee bucket first.
  const grossActivitiesUSD =
    activities.reduce((acc, a) => acc + a.usd, 0) +
    services.reduce((acc, s) => acc + s.usd, 0) +
    serviceFeeUSD;
  const totalDiscountUSD = discountUSD + spilloverDiscountUSD;
  const activitiesUSD = Math.max(0, grossActivitiesUSD - totalDiscountUSD);

  // Step 5 — ONE conversion: USD → CLP
  const activitiesCLP = Math.round(activitiesUSD * rate);

  // Step 6 — canonical integer CLP.
  // Any USD discount NOT absorbed by the activities bucket (e.g. a percentage promo
  // on a lodge-only booking, where activities = 0) must STILL reduce the total — it
  // spills onto the lodge CLP. Without this, a lodge discount evaporates and the
  // guest is charged full price. Floor the final amount at 0.
  const excessDiscountUSD = Math.max(0, totalDiscountUSD - grossActivitiesUSD);
  const excessDiscountCLP = Math.round(excessDiscountUSD * rate);
  const amountCLP = Math.max(0, lodgeCLP - lodgeDiscountCLP + activitiesCLP - excessDiscountCLP);

  // Step 7 — ONE conversion: CLP → USD (for display / Stripe)
  const amountUSD = Math.round((amountCLP / rate) * 100) / 100;

  // Build lines (each item in its native currency — no conversion in line items)
  const lines: ChargeLine[] = [
    ...lodges.map(
      (l): ChargeLine => ({
        ref: l.ref,
        kind: 'lodge',
        nativeCurrency: 'CLP',
        nativeAmount: l.clp,
      })
    ),
    ...activities.map(
      (a): ChargeLine => ({
        ref: a.ref,
        kind: 'activity',
        nativeCurrency: 'USD',
        nativeAmount: a.usd,
      })
    ),
    ...services.map(
      (s): ChargeLine => ({
        ref: s.ref,
        kind: 'service',
        nativeCurrency: 'USD',
        nativeAmount: s.usd,
      })
    ),
  ];

  return {
    version: 1,
    settlementCurrency,
    amountCLP,
    amountUSD,
    rate,
    rateSource,
    lines,
    lodgeCLP,
    activitiesUSD,
    activitiesCLP,
    serviceFeeUSD,
    discountUSD,
    ...(requestedDiscountCLP > 0 && { discountCLP: requestedDiscountCLP }),
  };
}
